import { useState } from 'react';

import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { Segmented } from '@/components/Segmented';
import { Sheet } from '@/components/Sheet';
import { toast } from '@/lib/toast';
import type { Client, Energy, Project, ProjectStatus } from './types';
import { useSaveProject } from './useClientAdmin';

const STATUSES: { value: ProjectStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'on_hold', label: 'On hold' },
  { value: 'completed', label: 'Done' },
];

const ENERGIES: { value: Energy; label: string }[] = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

interface Props {
  project: Project | null;
  client: Client;
  clients: Client[];
  onClose: () => void;
}

export function ProjectSheet({ project, client, clients, onClose }: Props) {
  const save = useSaveProject();
  const editing = project !== null;

  const [name, setName] = useState(project?.name ?? '');
  const [clientId, setClientId] = useState(project?.client_id ?? client.id);
  const [description, setDescription] = useState(project?.description ?? '');
  const [status, setStatus] = useState<ProjectStatus>(project?.status ?? 'active');
  const [energy, setEnergy] = useState<Energy>(project?.default_energy ?? 'medium');
  const [billable, setBillable] = useState(project?.default_billable ?? false);
  const [weeklyBudget, setWeeklyBudget] = useState(
    project?.weekly_time_budget_minutes ? String(project.weekly_time_budget_minutes / 60) : '',
  );
  const [rateOverride, setRateOverride] = useState(project?.hourly_rate_override ?? '');
  const [touched, setTouched] = useState(false);

  const nameError = touched && !name.trim() ? 'A project needs a name.' : undefined;

  const submit = () => {
    setTouched(true);
    if (!name.trim()) return;

    const weeklyHours = Number(weeklyBudget);
    save.mutate(
      {
        ...(editing ? { id: project.id } : {}),
        client_id: clientId,
        name: name.trim(),
        description: description.trim() || null,
        status,
        default_energy: energy,
        default_billable: billable,
        // Entered in hours because that is how people think about a week's
        // capacity; stored in minutes because everything else is.
        weekly_time_budget_minutes:
          weeklyBudget.trim() && weeklyHours > 0 ? Math.round(weeklyHours * 60) : null,
        hourly_rate_override: rateOverride.trim() || null,
      },
      {
        onSuccess: (saved) => {
          toast.show(editing ? `Saved ${saved.name}` : `Added ${saved.name}`);
          onClose();
        },
      },
    );
  };

  return (
    <Sheet
      title={editing ? 'Edit project' : `New project · ${client.name}`}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={save.isPending}>
            {editing ? 'Save' : 'Add project'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        <Field
          label="Name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => setTouched(true)}
          error={nameError}
          placeholder="Website redesign"
        />

        <div>
          <label htmlFor="project-client" className="mb-2 block text-small font-medium text-text-secondary">
            Client
          </label>
          <select
            id="project-client"
            value={clientId}
            onChange={(e) => setClientId(Number(e.target.value))}
            className="min-h-touch w-full rounded border border-border bg-surface-1 px-2 text-body outline-none focus-visible:ring-2 focus-visible:ring-ring focus:border-accent-border"
          >
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {editing && clientId !== project.client_id && (
            // Tasks carry a denormalised client_id, and the server keeps a
            // project with its client. Saying so beats a surprise later.
            <p className="mt-1 text-label text-warning">
              Moving this project does not move its existing tasks.
            </p>
          )}
        </div>

        <Segmented label="Status" value={status} options={STATUSES} onChange={setStatus} />

        <fieldset className="flex flex-col gap-3">
          <legend className="mb-1 text-small font-medium text-text-secondary">
            Defaults for new tasks
          </legend>

          <Segmented label="Energy" value={energy} options={ENERGIES} onChange={setEnergy} />

          <label className="flex min-h-touch items-center gap-3">
            <input
              type="checkbox"
              checked={billable}
              onChange={(e) => setBillable(e.target.checked)}
              className="size-icon accent-[var(--accent)]"
            />
            <span className="text-body">Billable by default</span>
          </label>
        </fieldset>

        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Hours per week"
            inputMode="decimal"
            value={weeklyBudget}
            onChange={(e) => setWeeklyBudget(e.target.value)}
            hint="Cap the scheduler respects."
            className="tabular"
          />
          <Field
            label="Rate override"
            inputMode="decimal"
            value={rateOverride}
            onChange={(e) => setRateOverride(e.target.value)}
            hint={`Falls back to ${client.hourly_rate ?? 'no rate'}.`}
            className="tabular"
          />
        </div>

        <div>
          <label
            htmlFor="project-description"
            className="mb-2 block text-small font-medium text-text-secondary"
          >
            Description
          </label>
          <textarea
            id="project-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full resize-y rounded border border-border bg-surface-1 p-2 text-body outline-none focus-visible:ring-2 focus-visible:ring-ring focus:border-accent-border"
          />
        </div>
      </div>
    </Sheet>
  );
}
